import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2, X } from "lucide-react";
import api from "@/lib/api";

const TYPE_META = {
  lead: { label: "Lead", tone: "bg-blue-100 text-blue-800" },
  quote: { label: "Offline Quote", tone: "bg-purple-100 text-purple-800" },
  claim: { label: "Claim", tone: "bg-rose-100 text-rose-800" },
  partner: { label: "Partner", tone: "bg-emerald-100 text-emerald-800" },
};

const GROUPS = [
  { key: "leads", title: "CRM Leads" },
  { key: "quotes", title: "Offline Quotes" },
  { key: "claims", title: "Claims" },
  { key: "partners", title: "Partners" },
];

export const GlobalSearch = ({ endpoint = "/search/global", placeholder = "Search by ref no (LD/OQ/CLM), name or mobile...", onSelect }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (q.trim().length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(endpoint, { params: { q: q.trim() } });
        setResults(r.data);
        setOpen(true);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [q, endpoint]);

  const total = results ? GROUPS.reduce((s, g) => s + (results[g.key]?.length || 0), 0) : 0;

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      <div className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2.5 shadow-sm focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-200 transition-colors">
        {loading ? <Loader2 className="w-4 h-4 text-amber-600 animate-spin shrink-0" /> : <Search className="w-4 h-4 text-stone-400 shrink-0" />}
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results) setOpen(true); }}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-stone-400"
        />
        {q && (
          <button data-testid="global-search-clear" onClick={() => { setQ(""); setResults(null); setOpen(false); }} className="text-stone-400 hover:text-stone-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && results && (
        <div data-testid="global-search-results" className="absolute z-50 mt-2 w-full rounded-2xl border border-stone-200 bg-white shadow-xl max-h-[28rem] overflow-y-auto">
          {total === 0 && (
            <div className="px-4 py-6 text-center text-sm text-stone-500" data-testid="global-search-empty">No matches for “{q}”</div>
          )}
          {GROUPS.map((g) => {
            const rows = results[g.key] || [];
            if (!rows.length) return null;
            return (
              <div key={g.key}>
                <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-stone-400">{g.title}</div>
                {rows.map((r) => {
                  const meta = TYPE_META[r.type] || TYPE_META.lead;
                  return (
                    <button
                      key={`${r.type}-${r.id}`}
                      data-testid={`search-result-${r.type}`}
                      onClick={() => { setOpen(false); onSelect && onSelect(r); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-amber-50 transition-colors flex items-center gap-3"
                    >
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${meta.tone}`}>{meta.label}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">
                          {r.ref_number && <span className="font-mono text-amber-700 mr-2">{r.ref_number}</span>}
                          {r.customer_name || "—"}
                        </div>
                        <div className="text-xs text-stone-500 truncate">
                          {[r.customer_mobile, r.product_name, r.partner_name && `Agent: ${r.partner_name}`].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {r.status && <span className="text-[10px] font-semibold text-stone-500 bg-stone-100 rounded-full px-2 py-0.5 shrink-0">{r.stage || r.status}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
          <div className="h-2" />
        </div>
      )}
    </div>
  );
};
